/**
 * BuilderPreviewProvider
 *
 * Provides the context needed for the production SDUI renderer to work
 * in the builder preview. Handles:
 * - Local input state management (updateContext actions)
 * - Action execution (client-side only)
 * - Navigation (local page switching)
 *
 * This allows the builder preview to use the same renderer as production
 * while managing state locally without server calls.
 */

import { ReactNode, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type {
  UIDefinition,
  BindingContext,
  ActionDefinition,
  ComputeAction,
} from '@fias/sdui-types';
import { evaluateExpression, ExpressionContext } from '@fias/expression-eval';
import { ArcheContext, ArcheContextValue } from '@/sdui/hooks/useArcheContext';
import { ArcheActionContext, ArcheActionValue } from '@/sdui/hooks/useArcheAction';
import { ArcheNavigationContext, ArcheNavigationValue } from '@/sdui/hooks/useArcheNavigation';
import { MAX_ACTION_DELAY_MS } from '@/lib/sdui-constants';

interface BuilderPreviewProviderProps {
  /** The UI definition to render */
  uiDefinition: UIDefinition | null;
  /** Initial input state */
  initialInput?: Record<string, unknown>;
  /** Theme mode */
  theme?: 'light' | 'dark';
  /** Children to render */
  children: ReactNode;
}

export function BuilderPreviewProvider({
  uiDefinition,
  initialInput = {},
  theme = 'light',
  children,
}: BuilderPreviewProviderProps) {
  // Input state for bindings and updateContext actions
  const [inputState, setInputState] = useState<Record<string, unknown>>(initialInput);

  // Navigation state
  const [currentPage, setCurrentPage] = useState<string>(uiDefinition?.defaultPage || 'main');
  const [pageHistory, setPageHistory] = useState<string[]>([]);

  // Action execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  // Track pending timeouts for cleanup
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Track which pages have already had their onLoad actions executed
  const pagesLoadedRef = useRef<Set<string>>(new Set());

  // Reset input state only when initialInput changes (new UI loaded),
  // NOT when uiDefinition changes (incremental tool updates).
  // This prevents wiping user-entered data on every edit_ui/add_action call.
  useEffect(() => {
    setInputState(initialInput);
  }, [initialInput]);

  // Reset navigation and action state when UI definition changes
  useEffect(() => {
    setCurrentPage(uiDefinition?.defaultPage || 'main');
    setPageHistory([]);
    setLastError(null);
    pagesLoadedRef.current.clear();
    // Clear pending timeouts
    pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    pendingTimeoutsRef.current.clear();
  }, [uiDefinition]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current.clear();
    };
  }, []);

  // Get action definition from UI
  const getActionDef = useCallback(
    (actionId: string): ActionDefinition | undefined => {
      if (!uiDefinition) return undefined;

      // Check global actions
      if (uiDefinition.globalActions?.[actionId]) {
        return uiDefinition.globalActions[actionId];
      }

      // Check current page actions
      const page = uiDefinition.pages?.[currentPage];
      if (page?.actions?.[actionId]) {
        return page.actions[actionId];
      }

      return undefined;
    },
    [uiDefinition, currentPage],
  );

  // Execute a single action
  const executeAction = useCallback(
    (actionId: string, actionDef: ActionDefinition) => {
      const runAction = () => {
        switch (actionDef.type) {
          case 'updateContext':
            if (actionDef.updates) {
              // Resolve any bindings in the updates
              const resolvedUpdates: Record<string, unknown> = {};
              for (const [key, value] of Object.entries(actionDef.updates)) {
                if (typeof value === 'string' && value.startsWith('{{') && value.endsWith('}}')) {
                  // Simple expression evaluation for common patterns
                  const expr = value.slice(2, -2).trim();

                  // Handle negation: !input.visible
                  if (expr.startsWith('!input.')) {
                    const path = expr.slice(7);
                    resolvedUpdates[key] = !inputState[path];
                  }
                  // Handle arithmetic: input.count + 1
                  else if (expr.includes('input.') && (expr.includes('+') || expr.includes('-'))) {
                    const match = expr.match(/input\.(\w+)\s*([+-])\s*(\d+)/);
                    if (match) {
                      const [, varName, op, num] = match;
                      const currentVal = (inputState[varName] as number) || 0;
                      resolvedUpdates[key] =
                        op === '+' ? currentVal + parseInt(num) : currentVal - parseInt(num);
                    } else {
                      resolvedUpdates[key] = value;
                    }
                  }
                  // Handle simple reference: input.something
                  else if (expr.startsWith('input.')) {
                    const path = expr.slice(6);
                    resolvedUpdates[key] = inputState[path];
                  } else {
                    resolvedUpdates[key] = value;
                  }
                } else {
                  resolvedUpdates[key] = value;
                }
              }
              setInputState((prev) => ({ ...prev, ...resolvedUpdates }));
            }
            break;

          case 'navigate':
            if (actionDef.targetPage) {
              setPageHistory((prev) => [...prev, currentPage]);
              setCurrentPage(actionDef.targetPage);
            }
            break;

          case 'compute': {
            // Execute compute action using expression evaluator
            const computeAction = actionDef as ComputeAction;
            const exprContext: ExpressionContext = {
              input: inputState,
              output: {},
              payload: {},
            };

            console.log(`[Preview] Compute action "${actionId}":`, {
              computations: computeAction.computations,
              condition: computeAction.condition,
              inputState,
            });

            // Check condition if present
            if (computeAction.condition) {
              const condResult = evaluateExpression(computeAction.condition, exprContext);
              console.log(`[Preview] Condition "${computeAction.condition}" result:`, condResult);
              if (!condResult.success || !condResult.value) {
                // Condition not met, skip computations
                console.log(`[Preview] Compute condition not met for "${actionId}"`);
                return;
              }
            }

            // Execute all computations and collect updates
            const computeUpdates: Record<string, unknown> = {};
            for (const [key, expression] of Object.entries(computeAction.computations)) {
              const result = evaluateExpression(expression, exprContext);
              console.log(`[Preview] Expression "${expression}" -> ${key}:`, result);
              if (result.success) {
                computeUpdates[key] = result.value;
              } else {
                console.warn(`[Preview] Compute expression failed for ${key}: ${result.error}`);
              }
            }

            console.log(`[Preview] Compute updates:`, computeUpdates);

            // Apply updates to input state
            if (Object.keys(computeUpdates).length > 0) {
              setInputState((prev) => ({ ...prev, ...computeUpdates }));
            }
            break;
          }

          case 'submit':
          case 'invoke':
            // These are server-side actions - log a message in preview
            console.log(`[Preview] Server action "${actionId}" would execute here`, actionDef);
            break;

          case 'aiInvoke':
            console.warn(
              `[Preview] AI action "${actionId}" requires a saved arche. AI streaming is not available in local-only preview mode.`,
            );
            break;

          case 'fileWrite':
            console.log(`[Preview] File write simulated for "${actionId}"`, actionDef);
            break;

          case 'fileRead':
            console.log(`[Preview] File read simulated for "${actionId}"`, actionDef);
            break;

          case 'fileDownload':
            console.log(`[Preview] File download simulated for "${actionId}"`, actionDef);
            break;

          default:
            console.warn(`[Preview] Unknown action type: ${actionDef.type}`);
        }
      };

      // Handle delay
      const delay = Math.min(actionDef.delayMs ?? 0, MAX_ACTION_DELAY_MS);
      if (delay > 0) {
        const timeout = setTimeout(() => {
          pendingTimeoutsRef.current.delete(timeout);
          runAction();
        }, delay);
        pendingTimeoutsRef.current.add(timeout);
      } else {
        runAction();
      }
    },
    [inputState, currentPage],
  );

  // Trigger action by ID
  const triggerAction = useCallback(
    (actionId: string, payload: Record<string, unknown>) => {
      const actionDef = getActionDef(actionId);
      if (!actionDef) {
        console.warn(`[Preview] Action not found: ${actionId}`);
        setLastError(`Action not found: ${actionId}`);
        return;
      }

      try {
        setIsExecuting(true);
        executeAction(actionId, { ...actionDef, ...payload });
        setLastError(null);
      } catch (err) {
        setLastError((err as Error).message);
      } finally {
        setIsExecuting(false);
      }
    },
    [getActionDef, executeAction],
  );

  // Execute action sequence
  const executeActionSequence = useCallback(
    (actionIds: string[]) => {
      console.log('[BuilderPreview] executeActionSequence called with:', actionIds);
      console.log('[BuilderPreview] Current page:', currentPage);
      console.log('[BuilderPreview] UI Definition:', uiDefinition);
      for (const actionId of actionIds) {
        const actionDef = getActionDef(actionId);
        console.log('[BuilderPreview] Action def for', actionId, ':', actionDef);
        if (actionDef) {
          executeAction(actionId, actionDef);
        } else {
          console.warn(`[Preview] Action not found in sequence: ${actionId}`);
        }
      }
    },
    [getActionDef, executeAction, currentPage, uiDefinition],
  );

  // Submit form (preview only logs)
  const submitForm = useCallback((formData: Record<string, unknown>) => {
    console.log('[Preview] Form submitted:', formData);
  }, []);

  // Update input value
  const updateInput = useCallback((key: string, value: unknown) => {
    setInputState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Navigate to page
  const navigateTo = useCallback(
    (pageId: string) => {
      setPageHistory((prev) => [...prev, currentPage]);
      setCurrentPage(pageId);
    },
    [currentPage],
  );

  // Go back
  const goBack = useCallback(() => {
    if (pageHistory.length > 0) {
      const previousPage = pageHistory[pageHistory.length - 1];
      setPageHistory((prev) => prev.slice(0, -1));
      setCurrentPage(previousPage);
    }
  }, [pageHistory]);

  // Execute onLoad actions when the current page changes
  useEffect(() => {
    if (!uiDefinition || !currentPage) return;

    const page = uiDefinition.pages?.[currentPage];
    if (!page?.onLoad) return;

    // Only run onLoad once per page per UI definition version
    if (pagesLoadedRef.current.has(currentPage)) return;
    pagesLoadedRef.current.add(currentPage);

    const onLoadIds = Array.isArray(page.onLoad) ? page.onLoad : [page.onLoad];
    const pageActions = page.actions as Record<string, ActionDefinition> | undefined;
    if (!pageActions) return;

    for (const actionId of onLoadIds) {
      const actionDef = pageActions[actionId];
      if (actionDef) {
        console.log(`[Preview] onLoad executing action: ${actionId}`);
        executeAction(actionId, actionDef);
      } else {
        console.warn(`[Preview] onLoad action not found: ${actionId}`);
      }
    }
  }, [currentPage, uiDefinition, executeAction]);

  // Build context values
  const archeContextValue: ArcheContextValue = useMemo(
    () => ({
      ui: uiDefinition,
      currentPage,
      context: {
        input: inputState,
        output: {},
        user: { id: 'preview-user', displayName: 'Preview User' },
        arche: { id: 'builder-preview', name: 'Builder Preview' },
        request: { id: 'preview-request', currentPage },
      } as BindingContext,
      requestId: null,
      archId: 'builder-preview',
      archeName: 'Builder Preview',
      isLoading: false,
      error: null,
    }),
    [uiDefinition, currentPage, inputState],
  );

  const actionContextValue: ArcheActionValue = useMemo(
    () => ({
      triggerAction,
      executeActionSequence,
      submitForm,
      updateInput,
      isExecuting,
      lastError,
      isAIStreaming: false, // AI streaming not supported in builder preview
      aiStreamingText: {},
      aiThinkingText: {},
      stopAIStreaming: () => {},
    }),
    [triggerAction, executeActionSequence, submitForm, updateInput, isExecuting, lastError],
  );

  const navigationContextValue: ArcheNavigationValue = useMemo(
    () => ({
      navigateTo,
      goBack,
      history: pageHistory,
      canGoBack: pageHistory.length > 0,
    }),
    [navigateTo, goBack, pageHistory],
  );

  return (
    <ArcheContext.Provider value={archeContextValue}>
      <ArcheActionContext.Provider value={actionContextValue}>
        <ArcheNavigationContext.Provider value={navigationContextValue}>
          <div className={theme === 'dark' ? 'dark' : ''}>{children}</div>
        </ArcheNavigationContext.Provider>
      </ArcheActionContext.Provider>
    </ArcheContext.Provider>
  );
}
